import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Hourglass, Calendar, Eye, Heart, Music, Mic, Search, Zap, Anchor } from 'lucide-react';
import { IchigoRule } from '../types';

const RULES: IchigoRule[] = [
  { id: 1, title: "Don't Postpone Special Moments", japanese: "特別な瞬間を後回しにしない", description: "Every opportunity is a one-time affair. If you don't take it now, it's lost forever. Life is a question of now or never.", icon: "Calendar" },
  { id: 2, title: "Live As If It Only Happens Once", japanese: "一期一会", description: "This gathering, this conversation, this sunset. It will never recur in exactly the same way. Cherish it.", icon: "Hourglass" },
  { id: 3, title: "Dwell In The Present", japanese: "現在に生きる", description: "The past and future are illusions of the mind. The only thing that is real is this very moment.", icon: "Anchor" },
  { id: 4, title: "Do Something You've Never Done", japanese: "新しいことに挑戦", description: "Routine kills the spirit of Ichigo Ichie. Seek novelty to awaken your brain to the present.", icon: "Zap" },
  { id: 5, title: "Practice Zazen", japanese: "座禅", description: "Sit on a cushion and observe the miracle of life. Detach yourself from the whirlwind of thoughts.", icon: "Eye" },
  { id: 6, title: "Apply Mindfulness to Five Senses", japanese: "五感", description: "Listen, look, touch, taste, and smell. Train yourself to appreciate the richness of sensory input.", icon: "Search" },
  { id: 7, title: "Notice Coincidences", japanese: "偶然", description: "Synchronicity is the universe winking at you. Pay attention to the magic of connection.", icon: "Heart" },
  { id: 8, title: "Make Every Gathering a Party", japanese: "会合", description: "Don't wait for a holiday or birthday. The simple act of meeting someone is cause enough for celebration.", icon: "Music" },
  { id: 9, title: "If You Don't Like It, Make Something Different", japanese: "変化", description: "You are not a victim of your reality. You are the architect of this moment. Transform it.", icon: "Mic" },
  { id: 10, title: "Hunt for Special Moments", japanese: "瞬間を探す", description: "They don't always come to you. You must seek them out with the curiosity of a child.", icon: "Search" }
];

const IconMap: Record<string, React.ReactNode> = {
  Calendar: <Calendar size={24} />,
  Hourglass: <Hourglass size={24} />,
  Anchor: <Anchor size={24} />,
  Zap: <Zap size={24} />,
  Eye: <Eye size={24} />,
  Search: <Search size={24} />,
  Heart: <Heart size={24} />,
  Music: <Music size={24} />,
  Mic: <Mic size={24} />,
};

const IchigoIchie: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-rose-50 text-rose-600 rounded-full mb-4">
          <Hourglass size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">Ichigo Ichie</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"One Time, One Meeting"</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          What we are experiencing right now will never happen again. 
          We must value each moment like a beautiful treasure.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-rose-300 shadow-md' : 'border-nunchi-200 hover:border-rose-200'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-rose-100 text-rose-600' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif">{rule.japanese}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-rose-100 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IchigoIchie;